import { Gradient, Shadow, Rect, Textbox } from 'fabric';
import type { FabricObject } from 'fabric';
import type {
  FillConfig,
  ShadowConfig,
  CanvasElement,
  ShapeElement,
  TextElement,
  ButtonElement,
} from '../types';
import { useStudioStore } from '../stores/studioStore';

// A resolved dynamic mapping must beat a static `shapeFill` / `textFill` /
// `buttonFill` visual config. Otherwise the legacy visual-apply pass overwrites
// the brand-primary color that `updateFabricObjectFromElement` just set from
// the mapping resolver, which is why "Rect 1" kept rendering its stored static
// fill instead of the school's primary color.
function hasResolvedMapping(element: CanvasElement, property: string): boolean {
  try {
    const value = useStudioStore.getState().getResolvedElementValue(element, property);
    return value !== null && value !== undefined && value !== '';
  } catch {
    return false;
  }
}

export const fillConfigToFabric = (
  fill: FillConfig,
  objectWidth: number,
  objectHeight: number
): string | Gradient<'linear'> | Gradient<'radial'> => {
  if (fill.type === 'solid' || !fill.gradient) {
    return fill.color;
  }

  const { gradient } = fill;

  if (gradient.type === 'linear') {
    const angleRad = (gradient.angle * Math.PI) / 180;
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);

    return new Gradient({
      type: 'linear',
      coords: {
        x1: objectWidth / 2 - (cos * objectWidth) / 2,
        y1: objectHeight / 2 - (sin * objectHeight) / 2,
        x2: objectWidth / 2 + (cos * objectWidth) / 2,
        y2: objectHeight / 2 + (sin * objectHeight) / 2,
      },
      colorStops: gradient.stops.map((stop) => ({
        offset: stop.offset,
        color: stop.color,
      })),
    });
  }

  if (gradient.type === 'radial') {
    const cx = objectWidth / 2;
    const cy = objectHeight / 2;
    const r = Math.max(objectWidth, objectHeight) / 2;

    return new Gradient({
      type: 'radial',
      coords: {
        x1: cx,
        y1: cy,
        r1: 0,
        x2: cx,
        y2: cy,
        r2: r,
      },
      colorStops: gradient.stops.map((stop) => ({
        offset: stop.offset,
        color: stop.color,
      })),
    });
  }

  return fill.color;
};

export const fabricFillToConfig = (fill: unknown): FillConfig => {
  if (typeof fill === 'string') {
    return { type: 'solid', color: fill };
  }

  const g = fill as Record<string, unknown> | null;
  if (g && g.type === 'linear' && g.colorStops) {
    const stops = (g.colorStops as Array<{ offset: number; color: string }>).map(
      (s) => ({ offset: s.offset, color: s.color })
    );
    const coords = g.coords as Record<string, number> | undefined;
    const dx = (coords?.x2 || 0) - (coords?.x1 || 0);
    const dy = (coords?.y2 || 0) - (coords?.y1 || 0);
    const angle = Math.round((Math.atan2(dy, dx) * 180) / Math.PI);

    return {
      type: 'gradient',
      color: stops[0]?.color || '#ffffff',
      gradient: { type: 'linear', angle: (angle + 360) % 360, stops },
    };
  }

  if (g && g.type === 'radial' && g.colorStops) {
    const stops = (g.colorStops as Array<{ offset: number; color: string }>).map(
      (s) => ({ offset: s.offset, color: s.color })
    );

    return {
      type: 'gradient',
      color: stops[0]?.color || '#ffffff',
      gradient: { type: 'radial', angle: 0, stops },
    };
  }

  return { type: 'solid', color: '#ffffff' };
};

export const shadowConfigToFabric = (shadow: ShadowConfig): Shadow | null => {
  if (!shadow.enabled) return null;

  return new Shadow({
    color: shadow.color,
    offsetX: shadow.offsetX,
    offsetY: shadow.offsetY,
    blur: shadow.blur,
  });
};

export const fabricShadowToConfig = (
  shadow: unknown
): ShadowConfig => {
  if (!shadow) {
    return { enabled: false, color: '#00000066', offsetX: 4, offsetY: 4, blur: 8 };
  }

  const s = shadow as Record<string, unknown>;
  return {
    enabled: true,
    color: (s.color as string) || '#00000066',
    offsetX: (s.offsetX as number) || 0,
    offsetY: (s.offsetY as number) || 0,
    blur: (s.blur as number) || 0,
  };
};

export const applyVisualsToFabricObject = (
  obj: FabricObject,
  element: CanvasElement
): void => {
  if (element.shadow?.enabled) {
    obj.set('shadow', shadowConfigToFabric(element.shadow));
  } else {
    obj.set('shadow', null);
  }

  try {
    if (element.type === 'shape') {
      const shapeEl = element as ShapeElement;
      if (shapeEl.shapeFill && !hasResolvedMapping(element, 'fillColor')) {
        const fabricFill = fillConfigToFabric(
          shapeEl.shapeFill,
          obj.width || 100,
          obj.height || 100
        );
        obj.set('fill', fabricFill);
      }
      if (shapeEl.border) {
        obj.set('stroke', shapeEl.border.color);
        obj.set('strokeWidth', shapeEl.border.width);
        if (shapeEl.border.style === 'dashed') {
          obj.set('strokeDashArray', [8, 4]);
        } else if (shapeEl.border.style === 'dotted') {
          obj.set('strokeDashArray', [2, 2]);
        } else {
          obj.set('strokeDashArray', undefined);
        }
        if (shapeEl.border.radius && obj instanceof Rect) {
          const r = shapeEl.border.radius;
          const uniformRadius = r.linked
            ? r.topLeft
            : Math.max(r.topLeft, r.topRight, r.bottomRight, r.bottomLeft);
          obj.set('rx', uniformRadius);
          obj.set('ry', uniformRadius);
        }
      }
    }

    if (element.type === 'text') {
      const textEl = element as TextElement;
      if (textEl.textFill && obj instanceof Textbox && !hasResolvedMapping(element, 'fontColor')) {
        const fabricFill = fillConfigToFabric(
          textEl.textFill,
          obj.width || 100,
          obj.height || 100
        );
        obj.set('fill', fabricFill);
      }
      if (textEl.strokeColor && textEl.textStrokeWidth) {
        obj.set('stroke', textEl.strokeColor);
        obj.set('strokeWidth', textEl.textStrokeWidth);
      }
    }

    if (element.type === 'button') {
      const btnEl = element as ButtonElement;
      // Button fill is owned exclusively by fabricSync updateFabricObjectFromElement,
      // which resolves Edit-As mapped values. Writing raw backgroundColor here would
      // stomp the resolved fill on every sync pass.
      if (btnEl.border) {
        obj.set('stroke', btnEl.border.color);
        obj.set('strokeWidth', btnEl.border.width);
        if (btnEl.border.radius && obj instanceof Rect) {
          const r = btnEl.border.radius;
          const uniformRadius = r.linked
            ? r.topLeft
            : Math.max(r.topLeft, r.topRight, r.bottomRight, r.bottomLeft);
          obj.set('rx', uniformRadius);
          obj.set('ry', uniformRadius);
        }
      }
    }
  } catch (err) {
    console.warn('[fabricVisuals] fill application failed for', element.type, err);
  }

  obj.setCoords();
};

export const readVisualsFromFabricObject = (
  obj: FabricObject
): Record<string, unknown> => {
  const visuals: Record<string, unknown> = {};

  visuals.opacity = obj.opacity ?? 1;
  visuals.shadow = fabricShadowToConfig(obj.shadow);

  const fillConfig = fabricFillToConfig(obj.fill);

  if (obj instanceof Rect) {
    visuals.border = {
      color: obj.stroke || '#ffffff33',
      width: obj.strokeWidth || 0,
      style: obj.strokeDashArray
        ? obj.strokeDashArray[0] > 4
          ? 'dashed'
          : 'dotted'
        : 'solid',
      radius: {
        topLeft: obj.rx || 0,
        topRight: obj.rx || 0,
        bottomRight: obj.rx || 0,
        bottomLeft: obj.rx || 0,
        linked: true,
      },
    };
  }

  if (obj instanceof Textbox) {
    visuals.letterSpacing = (obj.charSpacing || 0) / 10;
    visuals.lineHeight = obj.lineHeight || 1.2;
    if (obj.underline) visuals.textDecoration = 'underline';
    else if (obj.linethrough) visuals.textDecoration = 'line-through';
    else visuals.textDecoration = 'none';

    if (fillConfig.type === 'gradient') {
      visuals.textFill = fillConfig;
    }
  }

  return visuals;
};
